import Icon from '@/components/ui/AppIcon';

const pdfVolumeTiers = [
  { docs: '+500 dokumenter', price: '999 kr' },
  { docs: '+1.000 dokumenter', price: '1.799 kr' },
  { docs: '+2.500 dokumenter', price: '3.999 kr' },
  { docs: '+5.000 dokumenter', price: '6.999 kr' },
  { docs: '+10.000 dokumenter', price: '11.999 kr' },
];

const excelTiers = [
  { docs: '+5.000 dokumenter', price: '2.499 kr' },
];

export default function Pricing() {
  return (
    <section className="py-24 px-6 md:px-12 border-b border-border relative z-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 text-center reveal">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-4">
            04 / PRISER
          </span>
          <h2 className="text-4xl md:text-6xl text-foreground tracking-tight font-serif font-light mb-4">
            Enkel og skalerbar pricing
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Start med Core og tilføj det I har brug for. Ingen skjulte gebyrer – betal kun for det I bruger.
          </p>
        </div>

        {/* Core Plan */}
        <div className="mb-8 reveal delay-100">
          <div className="spotlight-card p-10 border-2 border-primary rounded-xl">
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-3 h-3 rounded-full bg-success animate-pulse" />
                    <span className="text-xs font-mono uppercase tracking-widest text-success">Grundprodukt</span>
                  </div>
                  <h3 className="text-3xl text-foreground font-serif font-light mb-2">Dataflowsuite Core</h3>
                  <p className="text-muted-foreground max-w-lg">
                    Alt hvad I behøver for at komme i gang med automatiseret dokumentflow.
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="flex items-baseline gap-2 justify-end">
                    <span className="text-5xl text-foreground font-serif font-light">4.999</span>
                    <span className="text-xl text-muted-foreground">kr / md</span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {[
                  'Portal med fuldt overblik',
                  'PDF → JSON konvertering',
                  'Datavalidering i 1 ERP system',
                  '4.000 dokumenter / md',
                  '1 connection inkluderet',
                  'API adgang',
                  'Standard support',
                ]?.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={18} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              <a
                href="#contact"
                className="inline-block px-8 py-4 bg-primary text-primary-foreground rounded-full font-medium hover:opacity-90 transition-opacity"
              >
                Kom i gang
              </a>
            </div>
          </div>
        </div>

        {/* Add-ons Grid */}
        <div className="mb-4">
          <h3 className="text-2xl text-foreground font-serif font-light mb-6 reveal">
            ➕ Udvidelser
          </h3>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Connections Add-on */}
          <div className="p-8 border border-border rounded-xl bg-card reveal delay-100">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
              <Icon name="LinkIcon" size={24} className="text-primary" />
            </div>
            <h3 className="text-xl text-foreground font-medium mb-2">🔌 Ekstra systemer</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Forbind flere systemer og valider data på tværs af platforme.
            </p>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-3xl text-foreground font-serif font-light">750</span>
              <span className="text-sm text-muted-foreground">kr / md pr. system</span>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex justify-between py-2 border-b border-border/50">
                <span>1 ERP system</span>
                <span className="text-success font-medium">Inkluderet</span>
              </div>
              <div className="flex justify-between py-2 border-b border-border/50">
                <span>2 systemer</span>
                <span>+750 kr</span>
              </div>
              <div className="flex justify-between py-2">
                <span>3 systemer</span>
                <span>+1.500 kr</span>
              </div>
            </div>
          </div>

          {/* PDF Volume Add-on */}
          <div className="p-8 border border-border rounded-xl bg-card reveal delay-200">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
              <Icon name="DocumentTextIcon" size={24} className="text-primary" />
            </div>
            <h3 className="text-xl text-foreground font-medium mb-2">📄 Flere PDF dokumenter</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Skalér dokumentvolumen efter jeres behov.
            </p>
            <div className="space-y-2">
              {pdfVolumeTiers?.map((tier, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-border/50 last:border-0">
                  <span className="text-sm text-muted-foreground">{tier?.docs}</span>
                  <span className="text-sm text-foreground font-medium">{tier?.price}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Excel Add-on */}
          <div className="p-8 border border-border rounded-xl bg-card reveal delay-300">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
              <Icon name="TableCellsIcon" size={24} className="text-primary" />
            </div>
            <h3 className="text-xl text-foreground font-medium mb-2">📊 Excel dokumenter</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Tilføj Excel-behandling til jeres dokumentflow.
            </p>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl text-foreground font-serif font-light">1.999</span>
              <span className="text-sm text-muted-foreground">kr / md</span>
            </div>
            <p className="text-xs text-success font-medium mb-6">Op til 5.000 Excel dokumenter</p>
            <div className="space-y-2 text-sm">
              {[
                'Excel → JSON konvertering',
                'Datavalidering i system',
                'Op til 5.000 dokumenter / md',
              ]?.map((f, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{f}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-border/50">
              <p className="text-xs text-muted-foreground">Ekstra forbrug:</p>
              {excelTiers?.map((tier, i) => (
                <div key={i} className="flex justify-between items-center mt-2">
                  <span className="text-xs text-muted-foreground">{tier?.docs}</span>
                  <span className="text-xs text-foreground font-medium">{tier?.price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Enterprise */}
        <div className="p-8 border border-border rounded-xl bg-card reveal delay-400">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🏢</span>
                <h3 className="text-2xl text-foreground font-serif font-light">Enterprise</h3>
              </div>
              <p className="text-muted-foreground mb-4">Har I større behov?</p>
              <div className="flex flex-wrap gap-3">
                {[
                  'Over 10.000 dokumenter / md',
                  'Mange integrationer',
                  'Skræddersyet setup',
                  'SLA & dedikeret support',
                ]?.map((f, i) => (
                  <span key={i} className="px-3 py-1 border border-border rounded-full text-xs text-muted-foreground">
                    {f}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0">
              <a
                href="#contact"
                className="inline-block px-8 py-4 border border-border text-foreground rounded-full font-medium hover:bg-card/50 transition-colors"
              >
                Kontakt os for en løsning →
              </a>
            </div>
          </div>
        </div>

        {/* Example Customer */}
        <div className="mt-12 p-8 border border-primary/20 rounded-xl bg-primary/5 reveal delay-200">
          <h3 className="text-xl text-foreground font-medium mb-6 flex items-center gap-2">
            <Icon name="CalculatorIcon" size={22} className="text-primary" />
            📈 Eksempel på realistisk kunde
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-sm text-muted-foreground mb-4 font-medium uppercase tracking-wider">Kundens setup</p>
              <div className="space-y-2">
                {[
                  { label: 'Core', value: '4.999 kr' },
                  { label: '+2 ekstra systemer', value: '1.500 kr' },
                  { label: '+5.000 PDF dokumenter', value: '6.999 kr' },
                  { label: 'Excel add-on', value: '1.999 kr' },
                ]?.map((item, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-sm text-muted-foreground">{item?.label}</span>
                    <span className="text-sm text-foreground">{item?.value}</span>
                  </div>
                ))}
                <div className="flex justify-between py-3 mt-2">
                  <span className="text-base text-foreground font-medium">Total</span>
                  <span className="text-xl text-primary font-serif font-light">15.497 kr / md</span>
                </div>
              </div>
            </div>

            {/* Competitor comparison */}
            <div className="border border-border/40 rounded-xl p-6 bg-background/40">
              <p className="text-sm text-muted-foreground mb-4 font-medium uppercase tracking-wider flex items-center gap-2">
                <span className="text-base">⚖️</span> Hvad koster det hos andre?
              </p>
              <div className="space-y-4">
                <div className="flex justify-between items-start py-2 border-b border-border/30">
                  <span className="text-sm text-muted-foreground">Opstartsomkostninger</span>
                  <span className="text-sm text-foreground text-right">20.000 – 35.000 kr</span>
                </div>
                <div className="flex justify-between items-start py-2 border-b border-border/30">
                  <span className="text-sm text-muted-foreground">Månedlig udgift<br /><span className="text-xs opacity-70">(uden validering)</span></span>
                  <span className="text-sm text-foreground text-right">4.000 – 7.000 kr / md</span>
                </div>
                <div className="flex justify-between items-start py-2 border-b border-border/30">
                  <span className="text-sm text-muted-foreground">Månedlig udgift<br /><span className="text-xs opacity-70">(med validering)</span></span>
                  <span className="text-sm text-foreground text-right">Op til 50.000 kr / md</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4 italic">
                Baseret på typiske priser hos andre udbydere på markedet.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}